import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, SectionCard, StatusPill, EmptyState } from "../components/Common";
import { toast } from "sonner";
import { FileText, Plus, Play, Upload, Download, Trash2, X, Edit, ChevronRight, ChevronLeft, CheckCircle2, Lock, Printer } from "lucide-react";
import { useAuth } from "../lib/auth";
import { printHmisPeriod } from "../lib/printHmisPeriod";

const FLOW = ["Draft", "Under Review", "Submitted", "Locked"];

export default function HMIS() {
  const [tab, setTab] = useState("periods");
  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="hmis-page">
      <PageHeader title="HMIS · Ministry of Health reporting" subtitle="Aggregate, review, adjust and export periodic returns" />
      <div className="flex gap-2 border-b border-slate-200">
        {[["periods","Periods"],["templates","Templates"]].map(([k,l]) => (
          <button key={k} data-testid={`hmis-tab-${k}`} onClick={()=>setTab(k)} className={`px-4 h-9 text-sm font-medium ${tab===k ? "border-b-2 border-[#1976D2] text-[#1976D2]" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>
      {tab === "periods" && <PeriodsTab />}
      {tab === "templates" && <TemplatesTab />}
    </div>
  );
}

// ================== TEMPLATES ==================
function TemplatesTab() {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const load = async () => { try { const {data} = await api.get("/reports/hmis/templates"); setItems(data||[]); } catch (e) {} };
  useEffect(() => { load(); }, []);

  if (detail) return <TemplateDetail templateId={detail} onBack={()=>{ setDetail(null); load(); }} />;

  return (
    <SectionCard title={`Templates · ${items.length}`} actions={
      <div className="flex gap-2">
        <button data-testid="hmis-import-btn" onClick={()=>setShowImport(true)} className="px-3 h-9 rounded-lg border border-slate-200 text-xs font-medium flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Import JSON</button>
        <button data-testid="hmis-new-template" onClick={()=>setShowNew(true)} className="px-3 h-9 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-xs font-semibold flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> New template</button>
      </div>
    }>
      {items.length === 0 ? (
        <EmptyState title="No templates yet" description="Create a template manually, or paste a JSON bundle exported from another VMS tenant / MoH." />
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 border-b"><tr><th className="text-left py-2 px-3">Code</th><th className="text-left">Name</th><th className="text-left">Version</th><th className="text-left">Period type</th><th></th></tr></thead>
          <tbody className="divide-y">
            {items.map(t => (
              <tr key={t.template_id} className="hover:bg-slate-50">
                <td className="py-2 px-3 tabular font-semibold text-[#1565C0]">{t.code}</td>
                <td>{t.name}</td>
                <td className="tabular">{t.version}</td>
                <td className="text-xs">{t.period_type}</td>
                <td className="text-right pr-3">
                  <button data-testid={`hmis-open-tpl-${t.template_id}`} onClick={()=>setDetail(t.template_id)} className="text-xs text-[#1976D2] font-semibold">Open →</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showNew && <NewTemplateModal onClose={()=>setShowNew(false)} onDone={()=>{setShowNew(false); load();}} />}
      {showImport && <ImportModal onClose={()=>setShowImport(false)} onDone={()=>{setShowImport(false); load();}} />}
    </SectionCard>
  );
}

function NewTemplateModal({ onClose, onDone }) {
  const [f, setF] = useState({ code:"HMIS_105", name:"", version:"1.0", period_type:"monthly", effective_from:"", effective_to:"" });
  const save = async () => {
    try { await api.post("/reports/hmis/templates", f); toast.success("Template created"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6" onClick={e=>e.stopPropagation()}>
        <div className="font-semibold mb-3">New template</div>
        <div className="grid grid-cols-2 gap-3">
          <Fld l="Code (HMIS_105)" v={f.code} on={v=>setF({...f,code:v})} />
          <Fld l="Version" v={f.version} on={v=>setF({...f,version:v})} />
          <Fld l="Name" v={f.name} on={v=>setF({...f,name:v})} span2 />
          <Sel l="Period type" v={f.period_type} on={v=>setF({...f,period_type:v})} opts={["monthly","weekly","quarterly","annual"]} />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-9 rounded-lg border border-slate-200 text-sm">Cancel</button>
          <button data-testid="hmis-save-template" onClick={save} className="px-4 h-9 rounded-lg bg-[#1976D2] text-white text-sm font-semibold">Create</button>
        </div>
      </div>
    </div>
  );
}

function ImportModal({ onClose, onDone }) {
  const [text, setText] = useState("");
  const submit = async () => {
    try {
      const bundle = JSON.parse(text);
      const { data } = await api.post("/reports/hmis/templates/import", bundle);
      toast.success(`Imported — template_id ${data.template_id}`);
      onDone();
    } catch (e) {
      if (e instanceof SyntaxError) toast.error("Invalid JSON");
      else toast.error(e?.response?.data?.detail || "Import failed");
    }
  };
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-2xl w-full p-6" onClick={e=>e.stopPropagation()}>
        <div className="font-semibold mb-2">Import template JSON</div>
        <div className="text-xs text-slate-500 mb-3">Paste a bundle exported from another VMS tenant or a MoH-supplied JSON. Structure: <code>{"{template, age_bands, sections[{title, indicators[]}]}"}</code></div>
        <textarea data-testid="hmis-import-json" value={text} onChange={e=>setText(e.target.value)} rows={14} className="w-full font-mono text-xs px-3 py-2 rounded-lg border border-slate-200" placeholder='{"template":{"code":"HMIS_105","name":"...","version":"2024.1","period_type":"monthly"},"age_bands":[{"label":"0-28d","min_months":0,"max_months":0}],"sections":[{"title":"OPD","indicators":[{"code":"1.1","label":"New attendance","disaggregation":"age_sex","source_rule":{"kind":"opd_attendance"}}]}]}' />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-9 rounded-lg border border-slate-200 text-sm">Cancel</button>
          <button data-testid="hmis-do-import" onClick={submit} className="px-4 h-9 rounded-lg bg-[#1976D2] text-white text-sm font-semibold">Import</button>
        </div>
      </div>
    </div>
  );
}

function TemplateDetail({ templateId, onBack }) {
  const [bundle, setBundle] = useState(null);
  const [showSec, setShowSec] = useState(false);
  const [showBand, setShowBand] = useState(false);
  const [showInd, setShowInd] = useState(null); // section_id
  const load = async () => { try { const { data } = await api.get(`/reports/hmis/templates/${templateId}`); setBundle(data); } catch {} };
  useEffect(() => { load(); }, [templateId]);
  if (!bundle) return <div className="p-8 text-slate-500">Loading…</div>;

  const t = bundle.template;
  const bands = bundle.age_bands || [];
  const sections = bundle.sections || [];
  const indsBySec = {}; (bundle.indicators || []).forEach(i => { (indsBySec[i.section_id] ||= []).push(i); });

  const exportJson = async () => {
    try {
      const { data } = await api.get(`/reports/hmis/templates/${templateId}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${t.code}_v${t.version}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };
  const delBand = async (id) => { if (!window.confirm("Delete band?")) return; await api.delete(`/reports/hmis/age-bands/${id}`); load(); };
  const delSec  = async (id) => { if (!window.confirm("Delete section and its indicators?")) return; await api.delete(`/reports/hmis/sections/${id}`); load(); };
  const delInd  = async (id) => { if (!window.confirm("Delete indicator?")) return; await api.delete(`/reports/hmis/indicators/${id}`); load(); };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button onClick={onBack} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Back</button>
        <div className="text-sm text-slate-400">/</div>
        <div className="font-semibold">{t.code} v{t.version} — {t.name}</div>
        <button data-testid="hmis-export-json" onClick={exportJson} className="ml-auto px-3 h-9 rounded-lg border border-slate-200 text-xs font-medium flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Export JSON</button>
      </div>

      <SectionCard title={`Age bands · ${bands.length}`} actions={<button onClick={()=>setShowBand(true)} data-testid="hmis-add-band" className="text-xs text-[#1976D2] font-semibold flex items-center gap-1"><Plus className="w-3 h-3" /> Add band</button>}>
        {bands.length === 0 ? <div className="p-4 text-sm text-slate-500">No age bands. Add at least one.</div> : (
          <table className="w-full text-sm">
            <tbody className="divide-y">
              {bands.map(b => (
                <tr key={b.band_id}><td className="py-2 px-3">{b.label}</td><td className="tabular text-xs text-slate-500">{b.min_months}–{b.max_months} months</td><td className="text-right pr-3"><button onClick={()=>delBand(b.band_id)}><Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-[#B71C1C]" /></button></td></tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      <SectionCard title={`Sections · ${sections.length}`} actions={<button data-testid="hmis-add-section" onClick={()=>setShowSec(true)} className="text-xs text-[#1976D2] font-semibold flex items-center gap-1"><Plus className="w-3 h-3" /> Add section</button>}>
        {sections.length === 0 ? <div className="p-4 text-sm text-slate-500">No sections yet.</div> : sections.map(sec => (
          <div key={sec.section_id} className="border-b last:border-0 p-4">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-800">{sec.title}</div>
              <div className="flex gap-2">
                <button data-testid={`hmis-add-ind-${sec.section_id}`} onClick={()=>setShowInd(sec.section_id)} className="text-xs text-[#1976D2] flex items-center gap-1"><Plus className="w-3 h-3" /> Add indicator</button>
                <button onClick={()=>delSec(sec.section_id)}><Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-[#B71C1C]" /></button>
              </div>
            </div>
            {(indsBySec[sec.section_id] || []).length === 0 ? <div className="text-xs text-slate-500 mt-2">No indicators.</div> : (
              <table className="w-full text-xs mt-2">
                <tbody className="divide-y">
                  {(indsBySec[sec.section_id] || []).map(ind => (
                    <tr key={ind.indicator_id}>
                      <td className="py-1 tabular font-semibold text-[#1565C0]">{ind.code}</td>
                      <td>{ind.label}</td>
                      <td className="text-slate-500">{ind.disaggregation}</td>
                      <td className="text-slate-500 tabular">{ind.source_rule?.kind || "—"}</td>
                      <td className="text-right"><button onClick={()=>delInd(ind.indicator_id)}><X className="w-3.5 h-3.5 text-slate-400 hover:text-[#B71C1C]" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </SectionCard>

      {showBand && <AddBandModal templateId={templateId} onClose={()=>setShowBand(false)} onDone={()=>{setShowBand(false); load();}} />}
      {showSec && <AddSectionModal templateId={templateId} onClose={()=>setShowSec(false)} onDone={()=>{setShowSec(false); load();}} />}
      {showInd && <AddIndicatorModal sectionId={showInd} onClose={()=>setShowInd(null)} onDone={()=>{setShowInd(null); load();}} />}
    </div>
  );
}

function AddBandModal({ templateId, onClose, onDone }) {
  const [f, setF] = useState({ label:"", min_months:0, max_months:0 });
  const save = async () => { try { await api.post(`/reports/hmis/templates/${templateId}/age-bands`, { ...f, min_months: Number(f.min_months), max_months: Number(f.max_months) }); toast.success("Band added"); onDone(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  return <Modal onClose={onClose} title="Add age band">
    <div className="grid grid-cols-2 gap-3">
      <Fld l="Label (e.g. 0–28 days)" v={f.label} on={v=>setF({...f,label:v})} span2 testId="band-label" />
      <Fld l="Min months (inclusive)" v={f.min_months} on={v=>setF({...f,min_months:v})} testId="band-min" />
      <Fld l="Max months (inclusive)" v={f.max_months} on={v=>setF({...f,max_months:v})} testId="band-max" />
    </div>
    <ModalBtns onClose={onClose} onSave={save} saveLabel="Add" saveTestId="band-save" />
  </Modal>;
}

function AddSectionModal({ templateId, onClose, onDone }) {
  const [f, setF] = useState({ title:"", order:0 });
  const save = async () => { try { await api.post(`/reports/hmis/templates/${templateId}/sections`, { ...f, order: Number(f.order) }); toast.success("Section added"); onDone(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  return <Modal onClose={onClose} title="Add section">
    <div className="grid grid-cols-2 gap-3">
      <Fld l="Title" v={f.title} on={v=>setF({...f,title:v})} span2 testId="sec-title" />
      <Fld l="Order" v={f.order} on={v=>setF({...f,order:v})} />
    </div>
    <ModalBtns onClose={onClose} onSave={save} saveLabel="Add" saveTestId="sec-save" />
  </Modal>;
}

function AddIndicatorModal({ sectionId, onClose, onDone }) {
  const [f, setF] = useState({ code:"", label:"", section_id: sectionId, order:0, disaggregation:"none", source_rule:{} });
  const [ruleKind, setRuleKind] = useState("none");
  const [ruleCodes, setRuleCodes] = useState("");
  const save = async () => {
    const src = ruleKind === "none" ? {} : (ruleKind === "icd10_count" ? { kind: "icd10_count", codes: ruleCodes.split(",").map(s=>s.trim()).filter(Boolean) } : { kind: ruleKind });
    try { await api.post("/reports/hmis/indicators", { ...f, order: Number(f.order), source_rule: src }); toast.success("Indicator added"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return <Modal onClose={onClose} title="Add indicator">
    <div className="grid grid-cols-2 gap-3">
      <Fld l="Code" v={f.code} on={v=>setF({...f,code:v})} testId="ind-code" />
      <Fld l="Order" v={f.order} on={v=>setF({...f,order:v})} />
      <Fld l="Label" v={f.label} on={v=>setF({...f,label:v})} span2 testId="ind-label" />
      <Sel l="Disaggregation" v={f.disaggregation} on={v=>setF({...f,disaggregation:v})} opts={["none","age_sex","sex_only","custom"]} />
      <Sel l="Source rule" v={ruleKind} on={setRuleKind} opts={["none","opd_attendance","icd10_count","admissions","discharges","deaths","lab_tests","dispensing","stock_out_days"]} />
      {ruleKind === "icd10_count" && <Fld l="ICD-10 codes (comma-separated)" v={ruleCodes} on={setRuleCodes} span2 />}
    </div>
    <ModalBtns onClose={onClose} onSave={save} saveLabel="Add" saveTestId="ind-save" />
  </Modal>;
}

// ================== PERIODS ==================
function PeriodsTab() {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = async () => { try { const {data} = await api.get("/reports/hmis/periods"); setItems(data||[]); } catch {} };
  useEffect(() => { load(); }, []);
  if (detail) return <PeriodDetail periodId={detail} onBack={()=>{setDetail(null); load();}} />;
  return (
    <SectionCard title={`Periods · ${items.length}`} actions={<button data-testid="hmis-new-period" onClick={()=>setShowNew(true)} className="px-3 h-9 rounded-lg bg-[#1976D2] text-white text-xs font-semibold flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> New period</button>}>
      {items.length === 0 ? <EmptyState title="No periods yet" description="Create a period against a template. Regeneration creates a new version — never overwrites." /> : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 border-b"><tr><th className="text-left py-2 px-3">Period</th><th className="text-left">Version</th><th className="text-left">Status</th><th className="text-left">Generated</th><th></th></tr></thead>
          <tbody className="divide-y">
            {items.map(p => (
              <tr key={p.period_id} className="hover:bg-slate-50">
                <td className="py-2 px-3 tabular">{p.period_start} → {p.period_end}</td>
                <td className="tabular">v{p.version || 1}</td>
                <td><StatusChip s={p.status} /></td>
                <td className="text-xs text-slate-500">{p.generated_at ? new Date(p.generated_at).toLocaleString() : "—"}</td>
                <td className="text-right pr-3"><button data-testid={`hmis-open-prd-${p.period_id}`} onClick={()=>setDetail(p.period_id)} className="text-xs text-[#1976D2] font-semibold">Open →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showNew && <NewPeriodModal onClose={()=>setShowNew(false)} onDone={()=>{setShowNew(false); load();}} />}
    </SectionCard>
  );
}

function NewPeriodModal({ onClose, onDone }) {
  const [templates, setTemplates] = useState([]);
  const [f, setF] = useState({ template_id:"", period_start:"", period_end:"" });
  useEffect(() => { api.get("/reports/hmis/templates").then(r=>setTemplates(r.data||[])).catch(()=>{}); }, []);
  const save = async () => { try { await api.post("/reports/hmis/periods", f); toast.success("Period created"); onDone(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  return <Modal onClose={onClose} title="New period">
    <div className="grid grid-cols-2 gap-3">
      <Sel l="Template" v={f.template_id} on={v=>setF({...f,template_id:v})} opts={templates.map(t=>[t.template_id, `${t.code} v${t.version} — ${t.name}`])} span2 />
      <Fld l="Period start (YYYY-MM-DD)" v={f.period_start} on={v=>setF({...f,period_start:v})} type="date" />
      <Fld l="Period end (YYYY-MM-DD)" v={f.period_end} on={v=>setF({...f,period_end:v})} type="date" />
    </div>
    <ModalBtns onClose={onClose} onSave={save} saveLabel="Create" saveTestId="period-save" />
  </Modal>;
}

function PeriodDetail({ periodId, onBack }) {
  const { user, organization } = useAuth();
  const [bundle, setBundle] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [adj, setAdj] = useState(null); // value being adjusted
  const load = async () => {
    try {
      const { data } = await api.get(`/reports/hmis/periods/${periodId}`);
      setBundle(data);
      if (data.period?.template_id) {
        const { data: t } = await api.get(`/reports/hmis/templates/${data.period.template_id}`);
        setTpl(t);
      }
    } catch {}
  };
  useEffect(() => { load(); }, [periodId]);
  if (!bundle || !tpl) return <div className="p-8 text-slate-500">Loading…</div>;
  const p = bundle.period;
  const readOnly = p.status === "Submitted" || p.status === "Locked";
  const canSubmit = ["hospital_admin","hospital_owner","super_admin"].includes(user?.role);

  const generate = async () => { try { await api.post(`/reports/hmis/periods/${periodId}/generate`); toast.success("Regenerated from clinical events"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const changeStatus = async (to) => { try { await api.post(`/reports/hmis/periods/${periodId}/status`, { to_status: to }); toast.success(`Moved to ${to}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const downloadCsv = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/hmis/periods/${periodId}/export.csv`;
    const token = localStorage.getItem("session_token");
    const r = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const blob = await r.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `hmis_${p.period_start}.csv`; a.click();
  };

  const values = bundle.values || [];
  const indsBySec = {}; (tpl.indicators || []).forEach(i => { (indsBySec[i.section_id] ||= []).push(i); });
  const valsByInd = {}; values.forEach(v => { (valsByInd[v.indicator_id] ||= []).push(v); });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={onBack} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Back</button>
        <div className="text-sm text-slate-400">/</div>
        <div className="font-semibold">{tpl.template.code} · {p.period_start} → {p.period_end} · v{p.version}</div>
        <StatusChip s={p.status} />
        <div className="ml-auto flex gap-2">
          {!readOnly && <button data-testid="hmis-generate" onClick={generate} className="px-3 h-9 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-xs font-semibold flex items-center gap-1.5"><Play className="w-3.5 h-3.5" /> Generate</button>}
          <button data-testid="hmis-download-csv" onClick={downloadCsv} className="px-3 h-9 rounded-lg border border-slate-200 text-xs font-medium flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Download CSV</button>
          <button data-testid="hmis-print-pdf" onClick={() => printHmisPeriod({ period: p, values, template: tpl.template, sections: tpl.sections, indicators: tpl.indicators, ageBands: tpl.age_bands, organization, generatedBy: user })} className="px-3 h-9 rounded-lg border border-slate-200 text-xs font-medium flex items-center gap-1.5"><Printer className="w-3.5 h-3.5" /> Print PDF</button>
          {p.status === "Draft" && <button data-testid="hmis-to-review" onClick={()=>changeStatus("Under Review")} className="px-3 h-9 rounded-lg border border-[#1976D2] text-[#1976D2] text-xs font-semibold">→ Under Review</button>}
          {p.status === "Under Review" && canSubmit && <button data-testid="hmis-submit" onClick={()=>changeStatus("Submitted")} className="px-3 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Submit</button>}
          {p.status === "Submitted" && canSubmit && <button data-testid="hmis-lock" onClick={()=>changeStatus("Locked")} className="px-3 h-9 rounded-lg bg-slate-800 text-white text-xs font-semibold flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Lock</button>}
        </div>
      </div>

      {tpl.sections.length === 0 ? <EmptyState title="No sections in template" description="Add sections and indicators to the template first, then regenerate." /> : tpl.sections.map(sec => (
        <SectionCard key={sec.section_id} title={sec.title}>
          {(indsBySec[sec.section_id] || []).length === 0 ? <div className="p-4 text-xs text-slate-500">No indicators.</div> : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b"><tr><th className="text-left py-2 px-3">Code</th><th className="text-left">Indicator</th><th className="text-left">Age band</th><th className="text-left">Sex</th><th className="text-right">Computed</th><th className="text-right">Adjusted</th><th></th></tr></thead>
              <tbody className="divide-y">
                {(indsBySec[sec.section_id] || []).flatMap(ind => (valsByInd[ind.indicator_id] || [{ value_id:`stub-${ind.indicator_id}`, indicator_id: ind.indicator_id, age_band:"—", sex:"—", computed_value:null, adjusted_value:null }]).map(v => (
                  <tr key={v.value_id} className={v.adjusted_value != null ? "bg-amber-50" : ""}>
                    <td className="py-1 px-3 tabular text-xs text-[#1565C0]">{ind.code}</td>
                    <td className="text-xs">{ind.label}</td>
                    <td className="text-xs text-slate-500">{v.age_band}</td>
                    <td className="text-xs text-slate-500">{v.sex}</td>
                    <td className="text-right tabular font-semibold">{v.computed_value ?? "—"}</td>
                    <td className="text-right tabular font-semibold text-amber-700">{v.adjusted_value != null ? v.adjusted_value : "—"}</td>
                    <td className="text-right pr-3">{!readOnly && v.value_id.startsWith("val_") && <button data-testid={`hmis-adjust-${v.value_id}`} onClick={()=>setAdj(v)} className="text-xs text-[#1976D2] font-semibold">Adjust</button>}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          )}
        </SectionCard>
      ))}

      {adj && <AdjustModal periodId={periodId} value={adj} onClose={()=>setAdj(null)} onDone={()=>{setAdj(null); load();}} />}
    </div>
  );
}

function AdjustModal({ periodId, value, onClose, onDone }) {
  const [f, setF] = useState({ adjusted_value: value.adjusted_value ?? value.computed_value ?? 0, reason: value.adjustment_reason || "" });
  const save = async () => {
    if (!f.reason.trim()) return toast.error("Reason is required");
    try { await api.post(`/reports/hmis/periods/${periodId}/adjust`, { value_id: value.value_id, adjusted_value: Number(f.adjusted_value), reason: f.reason }); toast.success("Adjustment recorded"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return <Modal onClose={onClose} title="Adjust value">
    <div className="text-xs text-slate-500 mb-3">Computed value: <b>{value.computed_value ?? "—"}</b>. Enter the adjusted value + a reason. Both are stored — the computed value is never overwritten.</div>
    <div className="grid grid-cols-2 gap-3">
      <Fld l="Adjusted value" v={f.adjusted_value} on={v=>setF({...f,adjusted_value:v})} testId="adj-value" />
      <div />
      <Fld l="Reason (required)" v={f.reason} on={v=>setF({...f,reason:v})} span2 testId="adj-reason" />
    </div>
    <ModalBtns onClose={onClose} onSave={save} saveLabel="Save adjustment" saveTestId="adj-save" />
  </Modal>;
}

// ================== Shared helpers ==================
function Modal({ onClose, title, children }) {
  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full p-6" onClick={e=>e.stopPropagation()}>
        <div className="font-semibold mb-3">{title}</div>
        {children}
      </div>
    </div>
  );
}
function ModalBtns({ onClose, onSave, saveLabel, saveTestId }) {
  return (
    <div className="mt-4 flex justify-end gap-2">
      <button onClick={onClose} className="px-4 h-9 rounded-lg border border-slate-200 text-sm">Cancel</button>
      <button data-testid={saveTestId} onClick={onSave} className="px-4 h-9 rounded-lg bg-[#1976D2] text-white text-sm font-semibold">{saveLabel}</button>
    </div>
  );
}
function Fld({ l, v, on, span2, type = "text", testId }) {
  return (
    <label className={`block ${span2 ? "col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{l}</span>
      <input data-testid={testId} type={type} value={v} onChange={e=>on(e.target.value)} className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
    </label>
  );
}
function Sel({ l, v, on, opts, span2 }) {
  const norm = opts.map(o => Array.isArray(o) ? o : [o, o]);
  return (
    <label className={`block ${span2 ? "col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{l}</span>
      <select value={v} onChange={e=>on(e.target.value)} className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
        <option value="">— pick —</option>
        {norm.map(([val,label]) => <option key={val} value={val}>{label}</option>)}
      </select>
    </label>
  );
}
function StatusChip({ s }) {
  const tone = s === "Draft" ? "bg-slate-100 text-slate-700" : s === "Under Review" ? "bg-amber-100 text-amber-800" : s === "Submitted" ? "bg-emerald-100 text-emerald-800" : "bg-slate-800 text-white";
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tone}`}>{s}</span>;
}
